import { HttpParams } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { HttpOptions, HttpStrategies, HttpStreamsService } from '@cbre/http';
import { CbreLogger, CbreLoggerToken } from '@cbre/logger';
import { forkJoin, Observable, of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';
import {
  AvailabilitySections,
  BrandSections,
  CompanySections,
  ContactSections,
  LeaseSections,
  PortfoliosaleSections,
  PropertySections,
  SaleSections,
  TenancySections,
  TimSections,
} from '../dictionaries/sections-dictionary';
import { CatalogSectionExtensionFrame, mapEnhancedSectionColumns } from '../extension';
import { extendColumns, extendSections } from '../extension/override-catalog';
import { CatalogResponse } from '../models/catalog-response';
import { Column, RawColumn } from '../models/column';
import { ColumnFactory } from '../models/column-factory';
import { FormControlOptions } from '../models/control-types';
import { Section } from '../models/section';
import { RelatedFieldRelationship } from '../models/column-composition-fns';
import { Data } from '../models/data';
import { mergeSectionColumnsAsList, mergeSectionColumnsAsMap } from '../utils';
import { connectCatalogProcessing, LovMap, mapCatalogResponseLovsValues } from '../utils/connect-catalog-lovs';
import { deepMerge } from '../utils/merge-utils';
import { CatalogFormBuilderService } from './catalog-form-builder.service';
import { doTransitiveSectionColumnsRelationsFiltering } from './transitive-section-column-filtering';
import { excludeColumnsByEntity } from '../utils/exclude-column-by-entity';

export enum CatalogMethod {
  Default = '',
  Insert = 'insert',
}

/**
 * Token replacer, function that uses regex to replace tokens with values
 * @example
 * tokenReplacer = compose(
 *   url => url.replace(/:userId/g, '123'),
 *   url => url.replace(/:market/g, 'Dallas')
 *  );
 * tokenReplacer('lookup/user/:userId/market/:market/') // 'lookup/user/123/market/Dallas/'
 */
export type TokenReplacerFn = (s: string) => string;

/**
 * No operation performed, used to fill the gaps when no tokenReplacer is provided, returns what it receives
 */
const defaultTokenReplacer: TokenReplacerFn = s => s;

export enum EntitySummarySection {
  property = PropertySections.Summary,
  company = CompanySections.Summary,
  contact = ContactSections.Summary,
  availability = AvailabilitySections.Summary,
  lease = LeaseSections.Summary,
  sale = SaleSections.Summary,
  portfoliosale = PortfoliosaleSections.Summary,
  tim = TimSections.Summary,
  tenancy = TenancySections.Summary,
  brand = BrandSections.Summary,
}

export interface ExtensionOptions<E, C = Data> {
  extension: CatalogSectionExtensionFrame<E, C>;
  context: C;
}

interface ColloquialAttributes {
  /**
   * Country Code Description
   */
  country_code_desc: string;
  /**
   * Country Language Description or Variation
   */
  language_desc: string;
}

export interface GetColumnOptions<C = Data> {
  /**
   * From retrieved catalog, retrieves all cols with lookupCategory, fetches the values and
   * maps to each corresponding column in the 'values' attribute of each column with lookupCategory
   */
  fetchAndMapLovs?: boolean;
  /**
   * It fetches all possible LOVs from BE cache mechanism. (Finally this would replace `fetchAndMapLovs` once other places adopt it)
   */
  fetchAndMapLovsFromCache?: boolean;
  /**
   * adds the "required" param query to the request, allowing
   * the BE to retrieve only the required fields & sections of
   * the catalog.
   */
  required?: boolean;
  /**
   *
   */
  filter?: string;
  /**
   * TokenReplacerFn, function that uses regex to replace tokens with values
   * @see `TokenReplacerFn`
   */
  tokenReplacer?: TokenReplacerFn;
  /**
   * If provided, response from catalog columns will return only the sections that
   * are included in selectSections array
   */
  selectSections?: number[];
  /**
   * Extension options for Section
   */
  extendSections?: ExtensionOptions<Section, C>;
  /**
   * Extension options for Columns
   */
  extendColumns?: ExtensionOptions<Column, C>;
  /**
   * Form control config extension
   */
  formControlOptions?: { [key: string]: FormControlOptions<C> };
  /**
   * Map uoms
   */
  mapUoms?: boolean;
  /**
   * This flag overcomes reads options.relatedFields and attempts to associate the column
   * instances into `Column.related` attribute. if true, it will map uoms and other relationships.
   * default value is `false`
   */
  mapAllRelations?: boolean;
  /**
   *
   */
  mapFormControls?: boolean;
  /**
   * Colloquial Attributes (country code and language descriptions)
   */
  colloquialAttributes?: ColloquialAttributes;
  /**
   * A flag to display the summary section. This is normally a dummy value (as
   * sections must always have a column at least). Defaults to true.
   */
  omitSummarySection?: boolean;
  /**
   * parameter to request the inclusion of specific section types in the catalog which are by default excluded
   */
  includeSectionsOfType?: number[];
}

export interface CatalogDictionary<T> {
  [key: string]: { [key: number]: T };
}

/**
 * Default options for getColumns method
 */
const defaultOpts: GetColumnOptions = Object.freeze({
  mapUoms: true,
  omitSummarySection: true,
});
/**
 * General catalog operations, fetch by entity
 */
@Injectable()
export class CatalogsService {
  constructor(
    protected httpStreamService: HttpStreamsService,
    private catalogFormBuilder: CatalogFormBuilderService,
    @Inject(CbreLoggerToken) private logger: CbreLogger
  ) {}
  /**
   * Returns an object which values contains array of Section for a given entity
   * @param  query - entity
   * @param  [method=""] - columns for insert mode (CatalogMethods.insert) or
   * update mode (CatalogMethods.Default)
   * @param options - An object that includes option flags or settings to modify
   * or specify the columns. Please refer to @type GetColumnOptions
   *
   * @return Map of entity sections
   */
  public getColumns<T extends Column = Column>(
    query?: string,
    method?: CatalogMethod,
    options?: GetColumnOptions
  ): Observable<CatalogResponse<T>> {
    // override defaultOpts
    options = { ...defaultOpts, ...(options || {}) };
    // set up params
    let params = new HttpParams();
    if (!!query) {
      params = params.set('query', query);
    }
    if (!!method) {
      params = params.set('method', method);
    }
    if (!!options.required) {
      params = params.set('required', options.required.toString());
    }
    if (!!options.filter) {
      params = params.set('filter', options.filter);
    }
    if (options.includeSectionsOfType) {
      params = params.set('transcientSections', options.includeSectionsOfType.join(','));
    }

    const paramsAsString = params.keys().length > 0 ? `?${decodeURIComponent(params.toString())}` : '';
    const url = `/lookup/columns${paramsAsString}`;
    // columns observable
    let catalogResponse$: Observable<CatalogResponse<T>> = this.httpStreamService.loadDataFor(url).pipe(
      mapEnhancedSectionColumns(),
      map(catalog => excludeColumnsByEntity(catalog as CatalogResponse<T>))
    );
    // connect options pipes
    catalogResponse$ = this.connectOptions$(catalogResponse$, options);
    // return CatalogResponse
    return catalogResponse$;
  }

  /**
   * Pipes processing depending on options values
   * @param catalogResponse$
   * @param options
   */
  private connectOptions$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    options?: GetColumnOptions
  ): Observable<CatalogResponse<T>> {
    if (options.selectSections) {
      catalogResponse$ = this.connectSectionSelection$(catalogResponse$, options.selectSections);
    }
    // extend Section attributes
    if (options.extendSections) {
      catalogResponse$ = this.connectSectionExtension$(catalogResponse$, options.extendSections);
    }
    // extend Column attributes
    if (options.extendColumns) {
      catalogResponse$ = this.connectColumnExtension$(catalogResponse$, options.extendColumns);
    }
    // return columns with lookupMapping
    if (options.fetchAndMapLovs || options.fetchAndMapLovsFromCache) {
      if (options.fetchAndMapLovsFromCache) {
        // call wrapper and fill in any column.values found out from wrapper call
        catalogResponse$ = this.connectCachedCatalogLovValues$(catalogResponse$);
      }
      // make each missing lookupCategory call to fill column.values (LOVs)
      catalogResponse$ = this.connectCatalogLovValues$(catalogResponse$, options);
    }
    // map uoms to it's master columns
    if (options.mapAllRelations || options.mapUoms) {
      catalogResponse$ = this.connectColumnUomMapping$(catalogResponse$, options.mapAllRelations);
    }
    if (options.mapFormControls) {
      catalogResponse$ = this.connectFormControls$<T>(catalogResponse$, options.formControlOptions || {});
    }
    if (options.colloquialAttributes != null) {
      catalogResponse$ = this.connectColloquialMapping$(catalogResponse$, options.colloquialAttributes);
    }
    // Omit summary section
    if (options.omitSummarySection) {
      catalogResponse$ = this.removeSummarySection$(catalogResponse$);
    }
    return catalogResponse$;
  }

  /**
   * Pipes a rxjs/map operation that filter out all sections NOT IN sectionIds
   * useful when you only need specific sections from a full catalog
   * @param catalogResponse$
   * @param sectionIds
   */
  private connectSectionSelection$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    sectionIds: number[]
  ): Observable<CatalogResponse<T>> {
    // select only sections in sectionIds set
    const selectSectionsTransformer = (sections: Array<Section<T>>) =>
      sections.filter(section => sectionIds.includes(section.sectionId));
    // return catalog responze containing only in sectionIds passed
    return catalogResponse$.pipe(map(catalog => connectCatalogProcessing(catalog, selectSectionsTransformer)));
  }

  /**
   * Extends Section attributes with given ExtensionFramework and context
   * provided in ExtensionOptions interface
   * @param catalogResponse$
   * @param extensionFw
   */
  private connectSectionExtension$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    extensionFw: ExtensionOptions<Section, Data>
  ): Observable<CatalogResponse<T>> {
    const sectionExtender = sections => extendSections(sections, extensionFw.extension, extensionFw.context);
    return catalogResponse$.pipe(map(catalog => connectCatalogProcessing(catalog, sectionExtender)));
  }

  /**
   * Extends Column attributes on each section with the given ExtensionFramework and context
   * provided in ExtensionOptions interface
   * @param catalogResponse$
   * @param extensionFw
   */
  private connectColumnExtension$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    extensionFw: ExtensionOptions<Column, Data>
  ): Observable<CatalogResponse<T>> {
    const columnExtender = sections =>
      sections.map((s: Section) => ({
        ...s,
        columns: extendColumns(s.columns, extensionFw.extension, extensionFw.context),
      }));
    return catalogResponse$.pipe(map(catalog => connectCatalogProcessing(catalog, columnExtender)));
  }

  /**
   * Attaches pipe processing to fetch lovs values and connect to each column in the
   * CatalogResponse
   * @param catalogResponse$
   * @param tokenReplacer
   */
  public connectCachedCatalogLovValues$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>
  ): Observable<CatalogResponse<T>> {
    // use lookup/wrapper/:entity to build a LOV Map  e.g. "{'lookup/yn': [{key: true, value:'Yes'}], ...more }"
    return catalogResponse$.pipe(switchMap(this._fetchLOVMapAndIncludeCatalogResponse));
  }

  /**
   * Facade method to transform catalog into {catalog lovMap} object where lovMap is product of invoking _doFetchLookupWrapper
   *
   * @param catalog
   * @returns
   */
  private _fetchLOVMapAndIncludeCatalogResponse = <T extends Column = Column>(catalog: CatalogResponse<T>) =>
    // user produced LOV Map to feed column.values into each catalog
    this._doFetchLookupWrapper(catalog).pipe(map(lovMap => mapCatalogResponseLovsValues(catalog, lovMap)));

  /**
   * Fetches LOV Map using `lookup/wrapper/:entity` e.g. "{'lookup/yn': [{key: true, value:'Yes'}], ...more }"
   * @param catalog
   * @returns
   */
  private _doFetchLookupWrapper<T extends Column = Column>(catalog: CatalogResponse<T>) {
    // request builder
    const _fetchLOVWrapperByEntity$ = entity => this.httpStreamService.loadDataFor(`/lookup/wrapper/${entity}`);

    // build wrapper by entity object (e.g. { property: { 'lookup/yn': [{Y}, {N}]}, ...more })
    const entityLOVsMap = Object.keys(catalog).map(entity => ({ [entity]: _fetchLOVWrapperByEntity$(entity) }));

    // resolve each entity within catalog as a single and collapsed map of LOVs
    return forkJoin(Object.assign({}, ...entityLOVsMap)).pipe(map(this._collapseCachedLovResponse));
  }

  /**
   * Builds each catalog entry into as a big and single map of LOVs
   * @param cachedLOVs
   * @returns
   */
  private _collapseCachedLovResponse = (cachedLOVs: Data): Data =>
    Object.entries(cachedLOVs).reduce((acc, [, next]) => ({ ...acc, ...next }), {});

  /**
   * Attaches pipe processing to fetch lovs values and connect to each column in the
   * CatalogResponse
   * @param catalogResponse$
   * @param tokenReplacer
   */
  public connectCatalogLovValues$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    options?: Pick<GetColumnOptions, 'tokenReplacer' | 'fetchAndMapLovsFromCache'>
  ): Observable<CatalogResponse<T>> {
    return catalogResponse$.pipe(
      switchMap(catalog => {
        // make dictionary of LOV request (produces object like: {'lookup/a':[a,b,c], 'lookup/b': [d,e,f]})
        const lovRequests$ = this.makeCatalogLovRequest$(catalog, options);

        // return catalog as is if dictionary of LOV request is empty (nothing to pull)
        if (Object.keys(lovRequests$).length === 0) return of(catalog);

        // resolve lov dictionary
        return forkJoin(lovRequests$).pipe(
          // map generated lov dictionary into catalog response (each section, each column to get corresponding lookup value inside the dictionary)
          map(lovsMap => mapCatalogResponseLovsValues<T>(catalog, lovsMap as LovMap))
        );
      })
    );
  }

  /**
   * Connect a switchMap pipe to fetch all LOVs required by the response of an `Observable<CatalogResponse<T>>`
   * @param columns$ - `Observable<CatalogResponse<T>>`
   * @param tokenReplacer - `TokenReplacerFn` function to interpolate values into the passed url
   * (e.g.:  `url => url.replace(/:userId/g, '123')` ).
   */
  public makeCatalogLovRequest$<T extends Column = Column>(
    catalog: CatalogResponse<T>,
    options?: Pick<GetColumnOptions, 'tokenReplacer' | 'fetchAndMapLovsFromCache'>
  ): { [key: string]: Observable<Data[]> } {
    // get all columns of all sections as a list of columns
    const allSections = Object.values(catalog).reduce((acc, sections) => [...acc, ...sections], []);
    const allColumns = mergeSectionColumnsAsList(allSections);
    // make column list and filter those columns with lookupCategory (required lovs) with no subscribeOn
    return this.makeCatalogLovRequests$(allColumns, options?.tokenReplacer ?? defaultTokenReplacer);
  }

  protected makeCatalogLovRequests$<T extends Column = Column>(
    columns: T[],
    tokenReplacer: TokenReplacerFn
  ): Record<string, Observable<Data[]>> {
    const lookupCategoryUrls: [string, Data][] = columns
      // columns with lookupCategory AND empty `values` (not previously filled through connectCachedCatalogLovValues)
      .filter(c => !!c.lookupCategory && c.values == null)
      // to lookupCategory url
      .map(c => [c.lookupCategory, { data: c.lovDefaultPayload ?? {} }]);

    // make a map of lookupCategory keys and Lovs observables
    // i.e {'/lookup/yn': observable}  => after forkJoin {'/lookup/yn': [{},{}]}
    return this._makeLookupMapFetcher(lookupCategoryUrls, tokenReplacer);
  }

  private _makeLookupMapFetcher = (
    lookupCategoryUrls: [string, Data][],
    tokenReplacer: TokenReplacerFn
  ): Record<string, Observable<Data[]>> => {
    const allRequests$ = lookupCategoryUrls.map(urlPayload => {
      const [url, options] = urlPayload;
      return { [url]: this.getLookupRequestStream$(url, tokenReplacer, options) };
    });

    return Object.assign({}, ...allRequests$);
  };

  /**
   * Gets or creates http lookup stream
   * @param url
   * @param tokenReplacer
   * @returns
   */
  private getLookupRequestStream$(url: string, tokenReplacer: TokenReplacerFn, options: HttpOptions) {
    return this.httpStreamService.loadDataFor(tokenReplacer(url), options, false).pipe(
      catchError(_error => {
        // TODO: Tech Debt(elias) implement @cbre/logger when transporters receive log level independently
        this.logger.warn('Unable to fetch LOVs values, returning empty options for safe processing');
        return of([]);
      })
    );
  }

  /**
   * Finds and map uom column to it's master column
   * @param catalogResponse$
   */
  private connectColumnUomMapping$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    mapAllRelations = false
  ): Observable<CatalogResponse<T>> {
    return catalogResponse$.pipe(
      map(catalog => {
        // creates a column dictionary (map) for easy access
        const catalogDictionary: CatalogDictionary<Column> = this.makeColumnsDictionaryMap(catalog);
        const sectionsWithUomMapped = Object.entries(catalog).map(([entity, sections]) => {
          // map and move related and uom to principal columns
          const colMappingOpts = { mapAllRelations, catalogDictionary };
          let nextSections = this.mapSectionsUomColumnsToValuesColumns(entity, sections, colMappingOpts);
          // filter columns to ensure column (p) to column(q) to section(r) transitive relationships as per MIQ-20392
          nextSections = doTransitiveSectionColumnsRelationsFiltering(nextSections);
          // break down each entity sections and map columns units
          return { [entity]: nextSections };
        });
        return Object.assign({}, ...sectionsWithUomMapped);
      })
    );
  }

  /**
   * Removes the summary section that comes from the DB with dummy fields,
   * which is not required on filters, but displays a short summary data
   * on record details.
   * @param catalogResponse$
   * @returns
   */
  private removeSummarySection$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>
  ): Observable<CatalogResponse<T>> {
    return catalogResponse$.pipe(
      map(catalog => {
        const filteredCatalog = Object.entries(catalog).map(([key, value]) => ({
          [key]: value.filter(section => !Object.values(EntitySummarySection).includes(section.sectionId)),
        }));
        return Object.assign({}, ...filteredCatalog);
      })
    );
  }

  /**
   * Creates a dictionary of columns by columnId grouped by entity
   * @example
   * {
   *   property: { 1: {...column1}, 2: column2 },
   *   lease: { 11: {...column11}, 21: column21 },
   *   // etc...
   * }
   * @param catalogResponse
   */
  protected makeColumnsDictionaryMap = <T extends Column = Column>(
    catalogResponse: CatalogResponse<T>
  ): CatalogDictionary<T> => {
    return Object.entries(catalogResponse).reduce(
      (acc, [entity, sections]) => ({ ...acc, [entity]: mergeSectionColumnsAsMap(sections) }),
      {}
    );
  };
  /**
   * Iterates each section and invokes singular version of this method mapSectionUomColumnsToValuesColumns to map all section columns and move
   * any uom column to it's master column (value column)
   * @param sections
   * @param entity
   * @param catalogDictionary
   * @returns
   */
  private mapSectionsUomColumnsToValuesColumns = (
    entity: string,
    sections: Section[],
    options: { mapAllRelations?: boolean; catalogDictionary: CatalogDictionary<Column> } = { catalogDictionary: {} }
  ): Section[] => sections.map(section => this.mapSectionUomColumnsToValuesColumns(section, entity, options));

  /**
   * Iterates each section columns and invokes moveUomColumnsToValueColumns to move any uom column to it's master column (value column)
   * @param sections
   * @param entity
   * @param catalogDictionary
   * @returns
   */
  private mapSectionUomColumnsToValuesColumns = (
    section: Section,
    entity: string,
    options: { mapAllRelations?: boolean; catalogDictionary: Data } = { catalogDictionary: {} }
  ): Section => ({
    ...section,
    columns: this.moveUomColumnsToValueColumns(section.columns, entity, options),
  });

  /**
   * Moves uom column to it's master column
   * @example
   * // given
   * columns = [
   *  {
   *    columnId:1,
   *    options: {
   *      relatedFields: [ { columnId: "16108", relationship: "UOM" }],
   *    }
   *  },
   *  {
   *    columnId: 16108,
   *  }
   * ] // size 2
   * // when
   *  moveUomColumnsToValueColumns(columns, 'x', {1: {columnId...}, 16108: {columnId: ...}})
   * // then
   * columns = [
   *  {
   *    columnId:1,
   *    options: {
   *      relatedFields": [ { columnId: "16108", relationship: "UOM" }],
   *    },
   *    unit: {
   *      columnId: 16108
   *    }
   *  },
   * ] // size 1
   * @param columns
   * @param entity
   * @param catalogDictionary
   * @returns
   */
  protected moveUomColumnsToValueColumns = (
    columns: Column[],
    entity: string,
    options: { mapAllRelations?: boolean; catalogDictionary: CatalogDictionary<Column> } = { catalogDictionary: {} }
  ): Column[] => {
    // filter callback to remove uom fields after mapping the uoms
    const isNotUom = col => col.field && !col.field.includes('_uom');
    let columnSieveFn = isNotUom;

    const { mapAllRelations = false } = options;
    if (mapAllRelations) {
      // a collapsable relation is such relation that groups/merges columns e.g. uom, estimatedF, or eventually opex fields
      const collapsableRelations = this._getCollapsableColumnsMap(columns);
      // make isNotInCollapsableRelation to exclude uoms and collapsable relations
      const isNotInCollapsableRelation = this._makeColumnNotInGivenDictionaryMapper(collapsableRelations);
      columnSieveFn = col => isNotUom(col) && isNotInCollapsableRelation(col);
    }
    const { catalogDictionary } = options;
    const entityColumns = catalogDictionary[entity];
    return (
      columns
        // map the uoms unit
        .map((col: Column) => ({
          ...col,
          unit: this.findAndMapUoms(col, entityColumns),
          related: this._getAllColumnRelationships(col, entityColumns)?.filter(
            // allow binding of only "collapsable" relationship types
            filterCol => this._validCollapsableRelationTypes[filterCol.relationType]
          ),
        }))
        // remove uoms once they are part of value columns
        .filter(columnSieveFn)
    );
  };

  /**
   * Returns a dictionary or map of columns that can be defined as related columns.
   * related columns are uoms (however this type fo relationship are handled differently),
   * estimatedF, and any new relation of this type where a column can have another as "parent"
   * or "master" relation
   * @param columns
   * @returns
   */
  private _getCollapsableColumnsMap = (columns: Column[]): { [key: string]: any } => {
    // map of "collapsable/merge-able" relationships
    // get list of collapsable columns map
    const collapsableColumns = columns
      .filter(col => col.options?.relatedFields != null)
      .map(col => {
        return Object.assign(
          {},
          ...col.options.relatedFields
            .filter(rel => this._validCollapsableRelationTypes[rel.relationship])
            .map(rel => ({ [rel.columnId]: rel.relationship }))
        );
      });
    // merge all collapsable columns found within columns
    return Object.assign({}, ...collapsableColumns);
  };

  /**
   * "collapsable/merge-able" relationships types
   */
  private get _validCollapsableRelationTypes() {
    return {
      [RelatedFieldRelationship.EstimatedF]: true,
      [RelatedFieldRelationship.InputGroup]: true,
    };
  }

  /**
   * Returns a function handy to filter columns that don't belongs to a a given dictionary
   */
  private _makeColumnNotInGivenDictionaryMapper = (relatedDictionary: { [key: string]: any }) => (column: Column) =>
    relatedDictionary[column.columnId] == null;

  /**
   * Attempts to find uom by common uom field names OR if uomId provided it uses the id to get it
   * from the digested dictionary
   * @param column
   * @param entity
   * @param catalogDictionary
   */
  private findAndMapUoms<T extends Column = Column>(column: T, entitySections: Record<string, T>) {
    let unit: T;
    if (column.uomId === -1 || column.field.includes('_uom')) {
      return undefined;
    }
    const columnRelations = column.getRelations();
    const warnUnitNotFound = foundUnit => {
      if (foundUnit == null) this.logger.warn(`couldn't find UOM for the ${column.field}`);
    };
    if (column.uomId) {
      unit = entitySections[column.uomId];
      warnUnitNotFound(unit);
    } else if (columnRelations[RelatedFieldRelationship.UOM]) {
      const uomRelation = columnRelations.hasOwnProperty(RelatedFieldRelationship.UOM)
        ? columnRelations[RelatedFieldRelationship.UOM]
        : null;
      if (!!uomRelation) {
        unit = entitySections[uomRelation];
        warnUnitNotFound(unit);
      }
    }
    // TODO: remove this logic and check once all the columns use relatedFields.
    if (!unit) {
      /**
       * https://jira.cbre.com/browse/MIQ-21722
       * get `prefix` and isolated field name so we can insert uom prefixes like 'ref_'
       * ex: field/space_size -> space_size
       * prefix = field/
       * fieldName = space_size
       * THEN we can search for
       * field/space_size_uom_desc (${column.field}_uom_desc`)
       * AND ALSO
       * field/ref_space_size_uom_desc (ctf.field === `${prefix}ref_${fieldName}_uom_desc`)
       */
      let prefix = '';
      let fieldName = column.field;

      const lastIndexAndToken = this._getLastIndexBeforeControlName(column.field);
      if (lastIndexAndToken > 0) {
        prefix = column.field.substring(0, lastIndexAndToken + 1);
        fieldName = column.field.substring(lastIndexAndToken + 1);
      }

      unit = Object.values(entitySections).find(
        ctf =>
          ctf.field === `${column.field}_uom_desc` ||
          ctf.field === `${column.field}_uom` ||
          ctf.field === `${column.field}_area_uom_desc` ||
          // ADDED THEN NEXT LINE DUE TO INCONSISTENCY OF UOM FIELD NAME: MIQ-3586
          ctf.field === `${prefix}ref_${fieldName}_uom_desc` ||
          ctf.field === `${prefix}ref_${fieldName}_yearly_uom_desc`
      );
    }
    return unit;
  }

  private _getLastIndexBeforeControlName(field: string): number {
    const lastIndexOfSlash = field.lastIndexOf('/');
    const lastIndexOfDot = field.lastIndexOf('.');
    return Math.max(lastIndexOfDot, lastIndexOfSlash);
  }

  /**
   * Returns a list of column instances declared in options.relatedFields mapped
   * from given catalogDictionary and entity
   * @param column
   * @param entity
   * @param catalogDictionary
   * @returns
   */
  private _getAllColumnRelationships<T extends Column = Column>(column: T, columnDictionary: Record<string, T>) {
    // get entity dictionary
    // return if no relatedFields declared or there is no columns to map
    if (column.options?.relatedFields == null || columnDictionary == null) return null;
    // return the list of column instances found for a given relatedFields list of given column
    return (
      column.options?.relatedFields
        // fill relationType and merge with column instance
        .map(r => ({ relationType: r.relationship, ...columnDictionary[r.columnId] }))
        // return only the columns that were able to be mapped
        .filter(c => c.columnId != null)
    );
  }

  /**
   * Scans builds and bind formControlOptions to sections and columns
   * @param catalogResponse$
   * @param formConfigs
   * @returns
   */
  private connectFormControls$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    formConfigs: { [key: string]: FormControlOptions<T> } = {}
  ): Observable<CatalogResponse<T>> {
    const columnExtender = sections => this.catalogFormBuilder.mapControlOptions(sections, formConfigs);
    return catalogResponse$.pipe(map(catalog => connectCatalogProcessing(catalog, columnExtender)));
  }

  /**
   * Returns an object which values contains array of Section with "default" columns for a given entity
   * @param entity - entity
   * @return Map of entity sections
   */
  public getDefaultColumns(entity: string, templateID?: string): Observable<any> {
    const baseUrl = '/lookup/columns/default?';
    let params = new HttpParams();
    if (entity != null) {
      params = params.set('entity', entity);
    }
    if (templateID != null) {
      params = params.set('templateID', templateID);
    }
    const uri = baseUrl + params.toString();
    return this.httpStreamService.loadDataFor(uri).pipe(map((cols: RawColumn[]) => cols.map(ColumnFactory)));
  }

  /**
   * Returns an array of column Id's which are default pinned columns for a given entity and template
   * @param entity - entity
   * @param templateID - templateID
   * @return default pinned columns
   */
  public getDefaultPinnedColumns(entity: string, templateID: string): Observable<any> {
    const baseUrl = '/lookup/columns/default/pins?';
    let params = new HttpParams();
    if (entity != null) {
      params = params.set('entity', entity);
    }
    if (templateID != null) {
      params = params.set('templateID', templateID);
    }
    const uri = baseUrl + params.toString();
    return this.httpStreamService.loadDataFor(uri);
  }

  /**
   * post call to check if attributes exist in given entity of user current catalog
   * return object with column and boolean value indicating if attributes exist EX: {123:true, 4566: false}
   * @param entity - entity
   * @param attrs - attributes to check
   * @return Observable<{ [key: number]: boolean }>
   */
  public checkAttrsExist(entity: string, attrs: number[]): Observable<{ [key: number]: boolean }> {
    const url = `/catalog/${entity}/checkAttrsExist`;
    return this.httpStreamService.getStream(url, HttpStrategies.POST, true, attrs);
  }

  /**
   * Creates the extender function to map the country and language description to "colloquial" columns
   * since every colloquial object needs to know the country and language description
   * @param catalogResponse$
   * @param colloquialAttributes
   */
  private connectColloquialMapping$<T extends Column = Column>(
    catalogResponse$: Observable<CatalogResponse<T>>,
    colloquialAttributes: ColloquialAttributes
  ): Observable<CatalogResponse<T>> {
    const columnExtender = (sections: Section[]) => this._mapColloquialAttrsToColumns(sections, colloquialAttributes);
    return catalogResponse$.pipe(map(catalog => connectCatalogProcessing(catalog, columnExtender)));
  }

  /**
   * Maps country and language description as staticPayload to columns that contains "colloquial" attribute within it's definition
   * since every colloquial object needs to know the country and language description
   * @param catalogResponse$
   * @param sections
   * @param colloquialAttributes
   * @returns
   */
  private _mapColloquialAttrsToColumns(
    sections: Section<Column>[],
    colloquialAttributes: ColloquialAttributes
  ): Section[] {
    const toCombinedColloquialAndColumnFn = this.makeColloquialAttributesMerger(colloquialAttributes);
    return sections.map(s => ({ ...s, columns: s.columns.map(toCombinedColloquialAndColumnFn) }));
  }

  /**
   * Returns a function that combines/merges column with {staticPayload: colloquialAttributes}.
   *
   * NOTE: staticPayload is not
   * used anymore in details page but it is still used for cloning purposes, if we manage to remove this dependency
   * from clone we no longer will need this mapping
   *
   * @param colloquialAttributes
   * @returns
   */
  private makeColloquialAttributesMerger(colloquialAttributes: ColloquialAttributes): (col: Column) => Column {
    return col => (col.isColloquial() ? (deepMerge(col, { staticPayload: colloquialAttributes }) as Column) : col);
  }
}
